import { NetworkStatus, useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { Button, MenuItem, Select, Theme, useTheme } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import * as React from 'react';
import DocumentTitle from 'react-document-title';
import { internalClientIds, JobType } from 'shared/common/job-constants';

import { defaultPeriods, Duration, getDurationOptions } from '../common/duration';
import { PageDialogLink } from '../common/page-dialog-link';
import {
    getPipelineStages,
    getPipelineWhere,
    PipelineStage,
    PipelineView,
    PipelineWhere,
    RecruiterPipelineRow
} from '../common/recruiter-pipeline';
import {
    RECRUITER_PIPELINE,
    RecruiterPipelineData,
    RecruiterPipelineVariables
} from '../graphql/queries/recruiter-pipeline';
import { useLocalStorage } from '../hooks/use-local-storage';
import { useReduxState } from '../hooks/use-redux';
import { CellValue, DataPanel, DataPanelColumn, dataPanelContainerStyles, selectorStyles } from './data-panel';
import { DurationSelector } from './duration-selector';
import { FunnelStat } from './funnel-stat';
import { JobsTypeSelector, jobTypesForFilter } from './jobs-type-selector';
import { UserMetrics } from './metrics/user-metrics';
import { NavigationSelect } from './navigation-select';
import { PipelineSelection, RecruiterPipelineCandidates } from './recruiter-pipeline-candidates';
import { RecruitingPageHeader, recruitingPageRoutes } from './recruiting-page-header';
import { TableColumnsSelector } from './table-columns-selector';

type Column = 'recruiter' | PipelineStage | 'allClientRounds';
interface PipelineColumn {
    id: Exclude<Column, 'recruiter'>;
    label: string;
}

const clientRoundColumns: PipelineStage[] = ['firstRounds', 'secondRounds', 'finalRounds'];
const allClientRoundsColumn: PipelineColumn = {
    id: 'allClientRounds',
    label: 'All Client Rounds'
};
const lastMonthDays = 30;
const lastWeekDays = 7;
const refreshInterval = 60000;
const lossPeriods = { ...defaultPeriods, rollingDays: [lastMonthDays, lastWeekDays] };

const styles = (theme: Theme) => css`
    ${dataPanelContainerStyles(theme)};
    .selectors > div {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 0;
    }
    .stat-main {
        font-weight: 500;
        font-size: 16px;
    }
    .funnel-stat {
        padding: 5px 0;
    }
    .recruiter-name {
        font-weight: 500;
        font-size: 14px;
        cursor: pointer;
        color: ${theme.palette.text.primary};

        &:hover {
            color: ${theme.palette.primary.main};
        }
    }
    @media (max-width: 900px) {
        padding: 20px;
    }
`;

export const RecruiterPipeline: React.FC = () => {
    const theme = useTheme();
    const [view, setView] = React.useState<PipelineView>('current');
    const jobStages = useReduxState((state) => state.appConstants.constants.jobStages);
    const stages = getPipelineStages(view).map((stage) => ({
        ...stage,
        label: jobStages.find((s) => s.name === stage.stage)?.label ?? stage.stage
    }));
    const availableColumns = stages.flatMap((stage) => {
        const column: PipelineColumn = { id: stage.id, label: stage.label };
        return stage.id === 'finalRounds' ? [column, allClientRoundsColumn] : [column];
    });
    const defaultColumns = availableColumns.reduce(
        (selected, column) => ({
            ...selected,
            [column.label]: true
        }),
        {} as { [label: string]: boolean }
    );
    const [duration, setDuration] = React.useState<Duration>(() => getDurationOptions(lossPeriods)[0]);
    const [jobTypes, setJobTypes] = useLocalStorage<JobType[]>(
        'recruiter-stats-job-types',
        jobTypesForFilter.get('all')
    );
    const [selectedColumns, setSelectedColumns] = useLocalStorage('recruiter-pipeline-columns', defaultColumns);
    const columnSelection = { ...defaultColumns, ...selectedColumns };
    const [selection, setSelection] = React.useState<PipelineSelection>();
    const { data, loading, error, networkStatus, refetch } = useQuery<
        RecruiterPipelineData,
        RecruiterPipelineVariables
    >(RECRUITER_PIPELINE, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        pollInterval: view === 'current' ? refreshInterval : 0,
        variables: {
            args: {
                endTime: duration.end,
                excludeClientIds: internalClientIds,
                isDisqualified: view === 'lost',
                jobTypes,
                startTime: duration.start
            }
        }
    });
    const asOf = data?.recruiters[0]?.asOf;
    const where = React.useMemo(
        () => getPipelineWhere(view, jobTypes, duration, asOf),
        [view, jobTypes, duration, asOf]
    );
    const columns: Array<DataPanelColumn<Column>> = [
        { id: 'recruiter', label: 'Recruiter' },
        ...availableColumns.filter((column) => columnSelection[column.label])
    ];

    React.useEffect(() => setSelection(undefined), [view, jobTypes, duration]);

    const handleViewChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setView(event.target.value as PipelineView);
    };
    const handleSelectColumn = (column: string, selected: boolean) => {
        setSelectedColumns((previous) => ({ ...previous, [column]: selected }));
    };
    const handleRetry = () => {
        refetch();
    };
    const handleCloseDetails = () => setSelection(undefined);
    const getStageCount = (row: RecruiterPipelineRow, stage: Exclude<Column, 'recruiter'>) =>
        stage === 'allClientRounds' ? clientRoundColumns.reduce((total, id) => total + row[id], 0) : row[stage];
    const recruitersWithCandidates = data?.recruiters.filter((row) =>
        stages.some((stage) => getStageCount(row, stage.id) > 0)
    );
    const getStageAverage = (stage: Exclude<Column, 'recruiter'>) => {
        if (stage === 'offers') return undefined;
        const recruiters = data?.recruiters ?? [];
        return recruiters.reduce((total, row) => total + getStageCount(row, stage), 0) / (recruiters.length || 1);
    };
    const getCellValue = (column: Column) => (row: RecruiterPipelineRow) =>
        column === 'recruiter' ? row.name : getStageCount(row, column);

    const handleCountClick = (rows: readonly RecruiterPipelineRow[], column: Exclude<Column, 'recruiter'>) => () => {
        const stage = stages.find((s) => s.id === column);
        const candidateFilters: PipelineWhere[] = [where, { assignee: { _in: rows.map((row) => row.id) } }];
        candidateFilters.push({
            stage:
                column === 'allClientRounds'
                    ? { _in: stages.filter((s) => clientRoundColumns.includes(s.id)).map((s) => s.stage) }
                    : { _eq: stage.stage }
        });
        const label = availableColumns.find((c) => c.id === column).label;
        const recruiterLabel = rows.length === 1 ? rows[0].name : 'All Selected Recruiters';
        setSelection({
            interviewKind: stage?.interviewKind,
            now: Date.now(),
            title: `${label} — ${recruiterLabel}${view === 'lost' ? ` · ${duration.period}` : ''}`,
            view,
            where: { _and: candidateFilters }
        });
    };
    const renderCount = (
        column: Exclude<Column, 'recruiter'>,
        rows: readonly RecruiterPipelineRow[],
        average?: number
    ) => {
        const count = rows.reduce((total, row) => total + getStageCount(row, column), 0);
        const label = availableColumns.find((c) => c.id === column).label;
        return (
            <FunnelStat
                data={{ value: count, periodBaseline: undefined, average }}
                description={label.toLowerCase()}
                period={view === 'lost' ? duration.period : 'the current pipeline'}
                baselinePeriod=""
                onClick={count ? handleCountClick(rows, column) : undefined}
            />
        );
    };
    const renderRecruiter = (_: CellValue, row: RecruiterPipelineRow) => (
        <PageDialogLink
            url={`/user/${row.username}/metrics`}
            Component={UserMetrics}
            componentProps={{ username: row.username }}
        >
            <span className="recruiter-name">{row.name}</span>
        </PageDialogLink>
    );
    const getCellRenderer = columns.reduce(
        (renderers, column) => {
            if (column.id !== 'recruiter') {
                const id = column.id;
                renderers[id] = (_: CellValue, row: RecruiterPipelineRow) =>
                    renderCount(id, [row], getStageAverage(id));
            }
            return renderers;
        },
        { recruiter: renderRecruiter } as {
            [key in Column]?: (value: CellValue, row: RecruiterPipelineRow) => JSX.Element;
        }
    );
    const getTotalValue = (column: Column, rows: readonly RecruiterPipelineRow[]) =>
        column === 'recruiter' ? <span className="recruiter-name">Total</span> : renderCount(column, rows);

    const viewSelector = (
        <Select
            value={view}
            onChange={handleViewChange}
            variant="outlined"
            inputProps={{ 'aria-label': 'Pipeline view' }}
        >
            <MenuItem value="current">Current</MenuItem>
            <MenuItem value="lost">Disqualified</MenuItem>
        </Select>
    );
    const periodSelector =
        view === 'lost' ? <DurationSelector selected={duration} onSelect={setDuration} periods={lossPeriods} /> : null;
    const columnSelector = (
        <TableColumnsSelector
            columns={availableColumns.map((column) => column.label)}
            selected={columnSelection}
            onSelect={handleSelectColumn}
        />
    );
    const filters = (
        <div css={selectorStyles}>
            <NavigationSelect routes={recruitingPageRoutes} />
            {viewSelector}
            {periodSelector}
            <JobsTypeSelector selected={jobTypes} onChange={setJobTypes} />
            {columnSelector}
        </div>
    );
    const retry = <Button onClick={handleRetry}>Retry</Button>;
    const errorMessage = error ? (
        <Alert severity="error" action={retry}>
            Unable to load recruiter pipeline.
        </Alert>
    ) : null;
    const table = !error ? (
        <DataPanel
            columns={columns}
            data={loading && networkStatus !== NetworkStatus.poll ? undefined : recruitersWithCandidates}
            getCellValue={getCellValue}
            getCellRenderer={getCellRenderer}
            getTotalValue={getTotalValue}
            sortableColumns={columns.map((c) => c.id)}
            filterableColumns={['recruiter']}
            initialSortColumn="recruiter"
            initialSortDirection="asc"
            hidePagination={true}
            pageSize={100}
            key={view}
        />
    ) : null;
    const details = selection ? (
        <RecruiterPipelineCandidates selection={selection} onClose={handleCloseDetails} />
    ) : null;
    const content = (
        <div css={styles(theme)}>
            <div className="selectors">{filters}</div>
            {errorMessage}
            <div className="data-panel">{table}</div>
            {details}
        </div>
    );
    return (
        <DocumentTitle title="Recruiter Pipeline">
            <div id="container">
                <RecruitingPageHeader />
                {content}
            </div>
        </DocumentTitle>
    );
};
